import React from 'react';
import { Skeleton } from '@mantine/core';
import { CiHome, CiRoute } from 'react-icons/ci';

import PageLayout from 'PageLayout';
import { EButton, EPageTitle, ESectionDivider, ESelect, ETextInput } from 'libs/ui';

import { IMyCharges, TApplyFor } from './hook/types';
import useMyCharges from './hook/useMyCharges';

export default function MyCharges() {

    const {
        allFeeTypes,
        serviceTypes,
        applyForTypes,
        bookingChargeCategories,
        myCharges,
        dispatchMyCharges,
        isSaving,
        saveCancellationCharges,
        isLoadingCancellationCharges,
        saveTransactionCharges,
        isLoadingTransactionCharges,
        saveModificationCharges,
        isLoadingModificationCharges,
        saveCreditCardOrIVRCharges,
        isLoadingCreditCardOrIVRCharges,
    } = useMyCharges();

    const applyOn=[
        {label:"Per Booking",value:"PER_BOOKING"},
        {label:"Per Passenger",value:"PER_PAX"},
        // {label:"Per Segment",value:"PER_SEGMENT"},
        // {label:"Per Sector",value:"PER_SECTOR"},
    ]

    return (
        <PageLayout>
            <EPageTitle
                title="My Charges"
                subTitle="Manage your charges here"
            />
            <div className="flex flex-col gap-10">
                <div className="grid grid-cols-3">
                    <ESelect
                        required
                        label="Select Service"
                        placeholder="Select"
                        value={myCharges.service}
                        data={serviceTypes}
                        onChange={(value) => value && dispatchMyCharges({
                            action: "UPDATE_SERVICE",
                            payload: value as IMyCharges["service"]
                        })}
                    />
                </div>

                {/* CANCELLATION CHARGES */}
                <div>
                    <ESectionDivider
                        title="Cancellation Charges"
                        end={
                            <div className="flex gap-3">
                                <EButton
                                    disabled={isLoadingCancellationCharges}
                                    loading={isSaving}
                                    onClick={saveCancellationCharges}
                                >Save</EButton>
                            </div>
                        }
                    />
                    {
                        isLoadingCancellationCharges
                            ?
                            <div className="grid grid-cols-2 gap-10">
                                <div className="flex flex-col gap-5">
                                    <Skeleton height={30} />
                                    <Skeleton height={60} />
                                    <Skeleton height={60} />
                                </div>
                                <div className="flex flex-col gap-5">
                                    <Skeleton height={30} />
                                    <Skeleton height={60} />
                                    <Skeleton height={60} />
                                </div>
                            </div>
                            :
                            <div className="grid grid-cols-2 gap-10">
                                <div className="flex flex-col gap-5">
                                    <div className="font-semibold flex items-center gap-1"><CiHome size={16} /> Domestic</div>
                                     <ESelect
                                        label="Apply On"
                                        placeholder="Select option"
                                        data={applyOn}
                                        value={myCharges.details.cancellationCharges.domesticChargeApplyFor}
                                        onChange={(value) => dispatchMyCharges({
                                            action: "UPDATE_CANCELLATION_CHARGES",
                                            payload: {
                                                field: "domesticChargeApplyFor",
                                                value: value as TApplyFor
                                            }
                                        })}
                                    />
                                    <ETextInput
                                        label="For Affiliate"
                                        placeholder="Enter amount"
                                        required={myCharges.rules.cancellationCharges.affiliateDomesticCharge === "YES"}
                                        error={myCharges.errors.cancellationCharges.affiliateDomesticCharge}
                                        value={myCharges.details.cancellationCharges.affiliateDomesticCharge ?? undefined}
                                        onChange={(e) => dispatchMyCharges({
                                            action: "UPDATE_CANCELLATION_CHARGES",
                                            payload: {
                                                field: "affiliateDomesticCharge",
                                                value: +e.target.value
                                            }
                                        })}
                                    />
                                    <ETextInput
                                        label="For Customer"
                                        placeholder="Enter amount"
                                        required={myCharges.rules.cancellationCharges.customerDomesticCharge === "YES"}
                                        error={myCharges.errors.cancellationCharges.customerDomesticCharge}
                                        value={myCharges.details.cancellationCharges.customerDomesticCharge ?? undefined}
                                        onChange={(e) => dispatchMyCharges({
                                            action: "UPDATE_CANCELLATION_CHARGES",
                                            payload: {
                                                field: "customerDomesticCharge",
                                                value: +e.target.value
                                            }
                                        })}
                                    />
                                   
                                </div>

                                <div className="flex flex-col gap-5">
                                    <div className="font-semibold flex items-center gap-1"><CiRoute size={16} />International</div>
                                     <ESelect
                                        label="Apply On"
                                        placeholder="Select option"
                                        data={applyOn}
                                        value={myCharges.details.cancellationCharges.internationalChargeApplyFor}
                                        onChange={(value) => dispatchMyCharges({
                                            action: "UPDATE_CANCELLATION_CHARGES",
                                            payload: {
                                                field: "internationalChargeApplyFor",
                                                value: value as TApplyFor
                                            }
                                        })}
                                    />
                                    <ETextInput
                                        label="For Affiliate"
                                        placeholder="Enter amount"
                                        required={myCharges.rules.cancellationCharges.affiliateInternationalCharge === "YES"}
                                        error={myCharges.errors.cancellationCharges.affiliateInternationalCharge}
                                        value={myCharges.details.cancellationCharges.affiliateInternationalCharge ?? undefined}
                                        onChange={(e) => dispatchMyCharges({
                                            action: "UPDATE_CANCELLATION_CHARGES",
                                            payload: {
                                                field: "affiliateInternationalCharge",
                                                value: +e.target.value
                                            }
                                        })}
                                    />
                                    <ETextInput
                                        label="For Customer"
                                        placeholder="Enter amount"
                                        required={myCharges.rules.cancellationCharges.customerInternationalCharge === "YES"}
                                        error={myCharges.errors.cancellationCharges.customerInternationalCharge}
                                        value={myCharges.details.cancellationCharges.customerInternationalCharge ?? undefined}
                                        onChange={(e) => dispatchMyCharges({
                                            action: "UPDATE_CANCELLATION_CHARGES",
                                            payload: {
                                                field: "customerInternationalCharge",
                                                value: +e.target.value
                                            }
                                        })}
                                    />

                                </div>
                            </div>
                    }
                </div>

                {/* TRANSACTION CHARGES */}
                <div>
                    <ESectionDivider
                        title="Transaction Charges"
                        end={
                            <div className="flex gap-3">
                                <EButton
                                    disabled={isLoadingTransactionCharges}
                                    loading={isSaving}
                                    onClick={saveTransactionCharges}
                                >Save</EButton>
                            </div>
                        }
                    />
                    {
                        isLoadingTransactionCharges
                            ?
                            <div className="grid grid-cols-2 gap-10">
                                <div className="flex flex-col gap-5">
                                    <Skeleton height={30} />
                                    <Skeleton height={60} />
                                    <Skeleton height={60} />
                                </div>
                                <div className="flex flex-col gap-5">
                                    <Skeleton height={30} />
                                    <Skeleton height={60} />
                                    <Skeleton height={60} />
                                </div>
                            </div>
                            :
                            <div className="grid grid-cols-2 gap-10">
                                <div className="flex flex-col gap-5">
                                    <div className="font-semibold flex items-center gap-1"><CiHome size={16} /> Domestic</div>
                                    <ETextInput
                                        label="For Affiliate"
                                        placeholder="Enter amount"
                                        required={myCharges.rules.transactionCharges.affiliateDomesticCharge === "YES"}
                                        error={myCharges.errors.transactionCharges.affiliateDomesticCharge}
                                        value={myCharges.details.transactionCharges.affiliateDomesticCharge ?? undefined}
                                        onChange={(e) => dispatchMyCharges({
                                            action: "UPDATE_TRANSACTION_CHARGES",
                                            payload: {
                                                field: "affiliateDomesticCharge",
                                                value: +e.target.value
                                            }
                                        })}
                                    />
                                    <ETextInput
                                        label="For Customer"
                                        placeholder="Enter amount"
                                        required={myCharges.rules.transactionCharges.customerDomesticCharge === "YES"}
                                        error={myCharges.errors.transactionCharges.customerDomesticCharge}
                                        value={myCharges.details.transactionCharges.customerDomesticCharge ?? undefined}
                                        onChange={(e) => dispatchMyCharges({
                                            action: "UPDATE_TRANSACTION_CHARGES",
                                            payload: {
                                                field: "customerDomesticCharge",
                                                value: +e.target.value
                                            }
                                        })}
                                    />
                                </div>

                                <div className="flex flex-col gap-5">
                                    <div className="font-semibold flex items-center gap-1"><CiRoute size={16} />International</div>
                                    <ETextInput
                                        label="For Affiliate"
                                        placeholder="Enter amount"
                                        required={myCharges.rules.transactionCharges.affiliateInternationalCharge === "YES"}
                                        error={myCharges.errors.transactionCharges.affiliateInternationalCharge}
                                        value={myCharges.details.transactionCharges.affiliateInternationalCharge ?? undefined}
                                        onChange={(e) => dispatchMyCharges({
                                            action: "UPDATE_TRANSACTION_CHARGES",
                                            payload: {
                                                field: "affiliateInternationalCharge",
                                                value: +e.target.value
                                            }
                                        })}
                                    />
                                    <ETextInput
                                        label="For Customer"
                                        placeholder="Enter amount"
                                        required={myCharges.rules.transactionCharges.customerInternationalCharge === "YES"}
                                        error={myCharges.errors.transactionCharges.customerInternationalCharge}
                                        value={myCharges.details.transactionCharges.customerInternationalCharge ?? undefined}
                                        onChange={(e) => dispatchMyCharges({
                                            action: "UPDATE_TRANSACTION_CHARGES",
                                            payload: {
                                                field: "customerInternationalCharge",
                                                value: +e.target.value
                                            }
                                        })}
                                    />
                                </div>
                            </div>
                    }
                </div>

                {/* MODIFICATION CHARGES */}
                <div>
                    <ESectionDivider
                        title="Modification Charges"
                        end={
                            <div className="flex gap-3">
                                <EButton
                                    disabled={isLoadingModificationCharges}
                                    loading={isSaving}
                                    onClick={saveModificationCharges}
                                >Save</EButton>
                            </div>
                        }
                    />
                    {
                        isLoadingModificationCharges
                            ?
                            <div className="grid grid-cols-2 gap-10">
                                <div className="flex flex-col gap-5">
                                    <Skeleton height={30} />
                                    <Skeleton height={60} />
                                    <Skeleton height={60} />
                                </div>
                                <div className="flex flex-col gap-5">
                                    <Skeleton height={30} />
                                    <Skeleton height={60} />
                                    <Skeleton height={60} />
                                </div>
                            </div>
                            :
                            <div className="grid grid-cols-2 gap-10">
                                <div className="flex flex-col gap-5">
                                    <div className="font-semibold flex items-center gap-1"><CiHome size={16} /> Domestic</div>
                                    <ETextInput
                                        label="For Affiliate"
                                        placeholder="Enter amount"
                                        required={myCharges.rules.modificationCharges.affiliateDomesticCharge === "YES"}
                                        error={myCharges.errors.modificationCharges.affiliateDomesticCharge}
                                        value={myCharges.details.modificationCharges.affiliateDomesticCharge ?? undefined}
                                        onChange={(e) => dispatchMyCharges({
                                            action: "UPDATE_MODIFICATION_CHARGES",
                                            payload: {
                                                field: "affiliateDomesticCharge",
                                                value: +e.target.value
                                            }
                                        })}
                                    />
                                    <ETextInput
                                        label="For Customer"
                                        placeholder="Enter amount"
                                        required={myCharges.rules.modificationCharges.customerDomesticCharge === "YES"}
                                        error={myCharges.errors.modificationCharges.customerDomesticCharge}
                                        value={myCharges.details.modificationCharges.customerDomesticCharge ?? undefined}
                                        onChange={(e) => dispatchMyCharges({
                                            action: "UPDATE_MODIFICATION_CHARGES",
                                            payload: {
                                                field: "customerDomesticCharge",
                                                value: +e.target.value
                                            }
                                        })}
                                    />
                                </div>

                                <div className="flex flex-col gap-5">
                                    <div className="font-semibold flex items-center gap-1"><CiRoute size={16} />International</div>
                                    <ETextInput
                                        label="For Affiliate"
                                        placeholder="Enter amount"
                                        required={myCharges.rules.modificationCharges.affiliateInternationalCharge === "YES"}
                                        error={myCharges.errors.modificationCharges.affiliateInternationalCharge}
                                        value={myCharges.details.modificationCharges.affiliateInternationalCharge ?? undefined}
                                        onChange={(e) => dispatchMyCharges({
                                            action: "UPDATE_MODIFICATION_CHARGES",
                                            payload: {
                                                field: "affiliateInternationalCharge",
                                                value: +e.target.value
                                            }
                                        })}
                                    />
                                    <ETextInput
                                        label="For Customer"
                                        placeholder="Enter amount"
                                        required={myCharges.rules.modificationCharges.customerInternationalCharge === "YES"}
                                        error={myCharges.errors.modificationCharges.customerInternationalCharge}
                                        value={myCharges.details.modificationCharges.customerInternationalCharge ?? undefined}
                                        onChange={(e) => dispatchMyCharges({
                                            action: "UPDATE_MODIFICATION_CHARGES",
                                            payload: {
                                                field: "customerInternationalCharge",
                                                value: +e.target.value
                                            }
                                        })}
                                    />
                                </div>
                            </div>
                    }
                </div>

                {/* CREDIT CARD / IVR CHARGES */}
                <div>
                    <ESectionDivider
                        title="Credit Card / IVR Charges"
                        end={
                            <div className="flex gap-3">
                                <EButton
                                    disabled={isLoadingCancellationCharges}
                                    loading={isSaving}
                                    onClick={saveCreditCardOrIVRCharges}
                                >Save</EButton>
                            </div>
                        }
                    />
                    {
                        isLoadingCreditCardOrIVRCharges
                            ?
                            <div className="grid grid-cols-2 gap-10">
                                <div className="flex flex-col gap-5">
                                    <Skeleton height={30} />
                                    <Skeleton height={60} />
                                    <Skeleton height={60} />
                                </div>
                                <div className="flex flex-col gap-5">
                                    <Skeleton height={30} />
                                    <Skeleton height={60} />
                                    <Skeleton height={60} />
                                </div>
                            </div>
                            :
                            <div className="grid grid-cols-2 gap-10">
                                <div className="flex flex-col gap-5">
                                    <div className="font-semibold flex items-center gap-1"><CiHome size={16} /> Domestic</div>
                                    <div className="flex gap-5">
                                        <ETextInput
                                            label="For Affiliate"
                                            placeholder="Enter amount"
                                            required={myCharges.rules.creditCardOrIVRCharges.affiliateDomesticCharge === "YES"}
                                            error={myCharges.errors.creditCardOrIVRCharges.affiliateDomesticCharge}
                                            value={myCharges.details.creditCardOrIVRCharges.affiliateDomesticCharge ?? undefined}
                                            onChange={(e) => dispatchMyCharges({
                                                action: "UPDATE_CREDIT_CARD_OR_IVR_CHARGES",
                                                payload: {
                                                    field: "affiliateDomesticCharge",
                                                    value: +e.target.value
                                                }
                                            })}
                                        />
                                        <ESelect
                                            label="Apply For"
                                            placeholder="Select"
                                            required
                                            error={undefined}
                                            data={["PER_BOOKING", "PER_PAX", "PER_SEGMENT", "PER_SECTOR"]}
                                            value={myCharges.details.creditCardOrIVRCharges.affiliateDomesticChargeApplyFor}
                                            onChange={(e) => e && dispatchMyCharges({
                                                action: "UPDATE_CREDIT_CARD_OR_IVR_CHARGES",
                                                payload: {
                                                    field: "affiliateDomesticChargeApplyFor",
                                                    value: e as TApplyFor
                                                }
                                            })}
                                        />
                                        <ESelect
                                            label="Charge Type"
                                            placeholder="Select"
                                            required
                                            error={undefined}
                                            data={["PERCENTAGE", "FIXED"]}
                                            value={myCharges.details.creditCardOrIVRCharges.affiliateDomesticChargeType}
                                            onChange={(e) => e && dispatchMyCharges({
                                                action: "UPDATE_CREDIT_CARD_OR_IVR_CHARGES",
                                                payload: {
                                                    field: "affiliateDomesticChargeType",
                                                    value: e as TApplyFor
                                                }
                                            })}
                                        />
                                    </div>

                                    <div className="flex gap-5">
                                        <ETextInput
                                            label="For Customer"
                                            placeholder="Enter amount"
                                            required={myCharges.rules.creditCardOrIVRCharges.customerDomesticCharge === "YES"}
                                            error={myCharges.errors.creditCardOrIVRCharges.customerDomesticCharge}
                                            value={myCharges.details.creditCardOrIVRCharges.customerDomesticCharge ?? undefined}
                                            onChange={(e) => dispatchMyCharges({
                                                action: "UPDATE_CREDIT_CARD_OR_IVR_CHARGES",
                                                payload: {
                                                    field: "customerDomesticCharge",
                                                    value: +e.target.value
                                                }
                                            })}
                                        />
                                        <ESelect
                                            label="Apply For"
                                            placeholder="Select"
                                            required
                                            error={undefined}
                                            data={["PER_BOOKING", "PER_PAX", "PER_SEGMENT", "PER_SECTOR"]}
                                            value={myCharges.details.creditCardOrIVRCharges.customerDomesticChargeApplyFor}
                                            onChange={(e) => e && dispatchMyCharges({
                                                action: "UPDATE_CREDIT_CARD_OR_IVR_CHARGES",
                                                payload: {
                                                    field: "customerDomesticChargeApplyFor",
                                                    value: e as TApplyFor
                                                }
                                            })}
                                        />
                                        <ESelect
                                            label="Charge Type"
                                            placeholder="Select"
                                            required
                                            error={undefined}
                                            data={["PERCENTAGE", "FIXED"]}
                                            value={myCharges.details.creditCardOrIVRCharges.customerDomesticChargeType}
                                            onChange={(e) => e && dispatchMyCharges({
                                                action: "UPDATE_CREDIT_CARD_OR_IVR_CHARGES",
                                                payload: {
                                                    field: "customerDomesticChargeType",
                                                    value: e as TApplyFor
                                                }
                                            })}
                                        />
                                    </div>
                                </div>

                                <div className="flex flex-col gap-5">
                                    <div className="font-semibold flex items-center gap-1"><CiRoute size={16} />International</div>
                                    <div className="flex gap-5">
                                        <ETextInput
                                            label="For Affiliate"
                                            placeholder="Enter amount"
                                            required={myCharges.rules.creditCardOrIVRCharges.affiliateInternationalCharge === "YES"}
                                            error={myCharges.errors.creditCardOrIVRCharges.affiliateInternationalCharge}
                                            value={myCharges.details.creditCardOrIVRCharges.affiliateInternationalCharge ?? undefined}
                                            onChange={(e) => {
                                                console.log(e.target.value);
                                                dispatchMyCharges({
                                                    action: "UPDATE_CREDIT_CARD_OR_IVR_CHARGES",
                                                    payload: {
                                                        field: "affiliateInternationalCharge",
                                                        value: +e.target.value
                                                    }
                                                })
                                            }}
                                        />
                                        <ESelect
                                            label="Apply For"
                                            placeholder="Select"
                                            required
                                            error={undefined}
                                            data={["PER_BOOKING", "PER_PAX", "PER_SEGMENT", "PER_SECTOR"]}
                                            value={myCharges.details.creditCardOrIVRCharges.affiliateInternationalChargeApplyFor}
                                            onChange={(e) => e && dispatchMyCharges({
                                                action: "UPDATE_CREDIT_CARD_OR_IVR_CHARGES",
                                                payload: {
                                                    field: "affiliateInternationalChargeApplyFor",
                                                    value: e as TApplyFor
                                                }
                                            })}
                                        />
                                        <ESelect
                                            label="Charge Type"
                                            placeholder="Select"
                                            required
                                            error={undefined}
                                            data={["PERCENTAGE", "FIXED"]}
                                            value={myCharges.details.creditCardOrIVRCharges.affiliateInternationalChargeType}
                                            onChange={(e) => e && dispatchMyCharges({
                                                action: "UPDATE_CREDIT_CARD_OR_IVR_CHARGES",
                                                payload: {
                                                    field: "affiliateInternationalChargeType",
                                                    value: e as TApplyFor
                                                }
                                            })}
                                        />
                                    </div>

                                    <div className="flex gap-5">
                                        <ETextInput
                                            label="For Customer"
                                            placeholder="Enter amount"
                                            required={myCharges.rules.creditCardOrIVRCharges.customerInternationalCharge === "YES"}
                                            error={myCharges.errors.creditCardOrIVRCharges.customerInternationalCharge}
                                            value={myCharges.details.creditCardOrIVRCharges.customerInternationalCharge ?? undefined}
                                            onChange={(e) => dispatchMyCharges({
                                                action: "UPDATE_CREDIT_CARD_OR_IVR_CHARGES",
                                                payload: {
                                                    field: "customerInternationalCharge",
                                                    value: +e.target.value
                                                }
                                            })}
                                        />
                                        <ESelect
                                            label="Apply For"
                                            placeholder="Select"
                                            required
                                            error={undefined}
                                            data={["PER_BOOKING", "PER_PAX", "PER_SEGMENT", "PER_SECTOR"]}
                                            value={myCharges.details.creditCardOrIVRCharges.customerInternationalChargeApplyFor}
                                            onChange={(e) => e && dispatchMyCharges({
                                                action: "UPDATE_CREDIT_CARD_OR_IVR_CHARGES",
                                                payload: {
                                                    field: "customerInternationalChargeApplyFor",
                                                    value: e as TApplyFor
                                                }
                                            })}
                                        />
                                        <ESelect
                                            label="Charge Type"
                                            placeholder="Select"
                                            required
                                            error={undefined}
                                            data={["PERCENTAGE", "FIXED"]}
                                            value={myCharges.details.creditCardOrIVRCharges.customerInternationalChargeType}
                                            onChange={(e) => e && dispatchMyCharges({
                                                action: "UPDATE_CREDIT_CARD_OR_IVR_CHARGES",
                                                payload: {
                                                    field: "customerInternationalChargeType",
                                                    value: e as TApplyFor
                                                }
                                            })}
                                        />
                                    </div>
                                </div>
                            </div>
                    }
                </div>
            </div>
        </PageLayout>
    )
}